import { useEffect } from 'react';
import { toMarathiDigits } from '../utils/marathi';

const SKIPPED_TAGS = new Set([
  'SCRIPT',
  'STYLE',
  'NOSCRIPT',
  'TEXTAREA',
  'INPUT',
  'CODE',
  'PRE',
]);

const shouldSkipNode = node => {
  const parent = node.parentElement;
  if (!parent) return true;
  if (SKIPPED_TAGS.has(parent.tagName)) return true;
  if (parent.tagName === 'FONT' || parent.closest('font')) return true;
  if (typeof document !== 'undefined' && (document.documentElement.classList.contains('translated-ltr') || document.documentElement.classList.contains('translated-rtl'))) return true;
  if (parent.isContentEditable) return true;
  if (parent.closest('[data-marathi-digits="false"]')) return true;
  if (parent.closest('[lang="en"]')) return true;
  if (parent.closest('.en-text, .gov-cert-english, .english-text, .notranslate')) return true;
  // If the text node itself contains Latin alphabet characters (English words, act titles, sections, etc.),
  // numbers in English text must never be converted to Marathi digits.
  const text = node.nodeValue || '';
  if (/[a-zA-Z]/.test(text)) return true;
  return false;
};

const normalizeTextNode = node => {
  if (!node?.nodeValue || !/\d/.test(node.nodeValue) || shouldSkipNode(node)) return;
  const nextValue = toMarathiDigits(node.nodeValue);
  if (nextValue !== node.nodeValue) node.nodeValue = nextValue;
};

const normalizeElement = root => {
  if (!root) return;

  if (root.nodeType === Node.TEXT_NODE) {
    normalizeTextNode(root);
    return;
  }

  if (root.nodeType !== Node.ELEMENT_NODE && root.nodeType !== Node.DOCUMENT_FRAGMENT_NODE) return;

  const walker = document.createTreeWalker(
    root,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode: node => (/\d/.test(node.nodeValue || '') && !shouldSkipNode(node)
        ? NodeFilter.FILTER_ACCEPT
        : NodeFilter.FILTER_REJECT),
    },
  );

  const nodes = [];
  while (walker.nextNode()) nodes.push(walker.currentNode);
  nodes.forEach(normalizeTextNode);
};

export default function useMarathiDisplayDigits() {
  useEffect(() => {
    normalizeElement(document.body);

    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.type === 'characterData') {
          normalizeTextNode(mutation.target);
          return;
        }

        mutation.addedNodes.forEach(normalizeElement);
      });
    });

    observer.observe(document.body, {
      childList: true,
      characterData: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, []);
}
